import { useState } from 'react'
import dayjs from 'dayjs'
import {
  AgxButton,
  AgxCollapsible,
  AgxDatePicker,
  AgxDivider,
  AgxSpacer,
  AgxTypography,
} from '@urbanx/agx.ui-components2'
import { Deposit, FormCondition, Condition } from '@urbanx/agx.campaigns.client'
import ConditionDate from '../components/ConditionDate'
import { EditConditionModal } from '../modals/EditConditionModal'
import { useFormConditionDueDate } from '../components/useFormConditionDueDate'
import slugify from 'slugify'
import TaskDueDateBadge from 'components/tasks/TaskDueDateBadge'
import { getContractInfoDueDateBadgeValue } from '../components/contractInformationDueDate'

interface ConditionPanelProps {
  id: string
  panelTitle: string
  contractDate?: Date
  condition?: FormCondition
  onChange: (condition: FormCondition | Deposit | undefined) => void
  onDelete?: () => void
  hideEditButton?: boolean
  hideConditionInfo?: boolean
  hideDeleteButton?: boolean
  onDateConditions?: Condition[]
  fromDateConditions?: Condition[]
  specialConditionIdToClauseNumberMap: {
    [key: string]: string
  }
}

export function ConditionPanel({
  id,
  panelTitle,
  contractDate,
  condition,
  onChange,
  onDelete,
  hideEditButton,
  hideConditionInfo,
  hideDeleteButton,
  onDateConditions,
  fromDateConditions,
  specialConditionIdToClauseNumberMap,
}: ConditionPanelProps) {
  const [showEditDateModal, setShowEditDateModal] = useState(false)
  const dueDate = useFormConditionDueDate(condition, contractDate)

  if (
    !condition?.condition ||
    [Condition.NotSubjectToFinance, Condition.NotApplicable].includes(
      condition.condition
    )
  )
    return

  const dueDateValue = dueDate?.dueDate
    ? dayjs(dueDate.dueDate).format('YYYY-MM-DD')
    : ''
  const dueDateBadgeValue = getContractInfoDueDateBadgeValue(dueDate?.dueDate)

  return (
    <AgxCollapsible
      key={`deposit-panel-${id}`}
      variant={'withDivider'}
      enableToggle
      open
      customTitle={
        <AgxSpacer
          fill
          direction="horizontal"
          gap="xs"
          contentAlignment="center"
        >
          <AgxTypography
            id={`condition-${slugify(panelTitle)}`}
            type={'title'}
            size={'large'}
            color="var(--text-primary)"
          >
            {panelTitle}
          </AgxTypography>
          {dueDateBadgeValue && (
            <TaskDueDateBadge dueDate={dueDateBadgeValue} isCompleted={false} />
          )}
        </AgxSpacer>
      }
      content={
        <>
          {!hideConditionInfo && condition && (
            <>
              <AgxSpacer
                direction="horizontal"
                gap="spacing-md"
                fill
                padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
              >
                <ConditionDate
                  id={id}
                  condition={condition}
                  specialConditionIdToClauseNumberMap={
                    specialConditionIdToClauseNumberMap ?? {}
                  }
                />
              </AgxSpacer>
              <AgxDivider variant="horizontal" />
            </>
          )}
          <AgxSpacer
            direction="horizontal"
            gap="spacing-md"
            fill
            padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
            contentAlignment="end"
          >
            <AgxDatePicker
              id={`${id}-due-date`}
              stretch
              disabled={true}
              optionalText=""
              label="Due Date"
              key={dueDateValue}
              value={dueDateValue}
            />
            {!hideEditButton && (
              <AgxButton
                id={`${id}-edit-due-date`}
                variant="hollow"
                text="Edit"
                onClick={() => {
                  setShowEditDateModal(true)
                }}
              />
            )}
          </AgxSpacer>
          {(!hideConditionInfo || !hideDeleteButton) && (
            <AgxSpacer
              direction="horizontal"
              gap="spacing-md"
              fill
              padding={{ top: 'spacing-md', bottom: 'spacing-md' }}
              contentAlignment="end"
              contentJustification="end"
            >
              {!hideDeleteButton && (
                <AgxButton
                  id={`${id}-delete`}
                  variant="link"
                  danger
                  text="Delete"
                  onClick={onDelete}
                />
              )}
            </AgxSpacer>
          )}
          {showEditDateModal && condition && (
            <EditConditionModal
              id={id}
              modalTitle="Edit Condition"
              formCondition={condition}
              onDateConditions={
                onDateConditions ?? [
                  Condition.OnSpecificDate,
                  Condition.UponSatisfactionOfSpecialCondition,
                ]
              }
              fromDateConditions={
                fromDateConditions ?? [
                  Condition.NdaysFromDate,
                  Condition.UponSatisfactionOfSpecialCondition,
                ]
              }
              openModal={setShowEditDateModal}
              onChange={onChange}
            />
          )}
        </>
      }
    ></AgxCollapsible>
  )
}
